import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { OctagonX } from "lucide-react";
import { useContext, useState } from "react";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { useTerminateFlow } from "../hooks/use-terminate-flow.hook";
import ActionConfirmModal from "./action-confirm-modal";
import { destructiveButtonSx } from "./field-shell";

/**
 * §Terminate: "the client decided not to continue" — flags the application TERMINATED in WM, which
 * halts the flow and hides the case from every CRM surface (this tab then shows the start-flow state).
 *
 * Gated on the UMS passport permission `can-terminate-nawy-now-flow` via `userHasPermission`,
 * deliberately NOT `<PermissionGuard>`: its `crm-super-admin` bypass would show the button to users
 * WM will 403 (WM only recognises its OWN super-admin group). Button visibility must equal the grant.
 *
 * Restoring is not offered here — that is a WM-super-admin action in the WM app.
 */
const TerminateFlowButton = ({ caseUid }: { caseUid: string }) => {
  const { permissions } = useContext(NawyPassportContext);
  const [confirming, setConfirming] = useState(false);
  const terminate = useTerminateFlow();

  const allowed = userHasPermission(
    { name: PermissionsEnum.NAWY_NOW_TERMINATE_FLOW, level: PermissionLevel.WRITE },
    permissions
  );
  if (!allowed) return null;

  return (
    <>
      <Tooltip title="The client decided not to continue — stop this flow and remove it from the CRM">
        {/* span: MUI cannot attach a tooltip to a disabled button */}
        <span style={{ display: "inline-flex", flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={terminate.isPending}
            sx={destructiveButtonSx}
            startIcon={
              terminate.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <OctagonX size={14} />
              )
            }
            onClick={(event) => {
              // the sticky header row sits inside clickable surfaces on mobile — never bubble
              event.stopPropagation();
              setConfirming(true);
            }}
          >
            Terminate flow
          </Button>
        </span>
      </Tooltip>
      <ActionConfirmModal
        request={
          confirming
            ? {
                title: "Terminate Nawy Now flow?",
                message:
                  "This stops the loan process for this lead and removes it from the CRM. All open tasks close and the teams stop working it. A new flow can be started later; only a Workflow Manager admin can restore this one.",
                destructive: true,
                confirmLabel: "Terminate",
                commentMode: "optional",
                commentLabel: "Reason (optional)",
              }
            : null
        }
        busy={terminate.isPending}
        caseUid={caseUid}
        attachmentContext={{kind: "case-termination"}}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason, attachmentUids) =>
          terminate.mutateAsync({ caseUid, reason, attachmentUids })
        }
      />
    </>
  );
};

export default TerminateFlowButton;
